import { ComputedRef } from 'vue'

import { CategoryRating } from '@/domain/entities/dashboard/categoryRating'
import { User } from '@/domain/entities/user/user'
import { DisplayLanguageType } from '@/domain/value_objects/language/displayLanguageType'
import { ProgramLanguageType } from '@/domain/value_objects/language/programLanguageType'
import { CategoryRatingViewModel } from '@/presentation/viewmodels/dashboard/categoryRatingViewModel'
import { HowCategoryRatingViewModel } from '@/presentation/viewmodels/dashboard/howCategoryRatingViewModel'
import { categoryLabelsMultipleEN, categoryLabelsMultipleJP } from '@/util/dashboard/categoryLabels'
import { getRadarChartOptions } from '@/util/dashboard/radarChartOptions'

export class HowCategoryRatingPresenter {
  private viewModel: HowCategoryRatingViewModel
  private categoryRatingViewModel: CategoryRatingViewModel

  constructor(
    categoryRatingViewModel: CategoryRatingViewModel,
    viewModel: HowCategoryRatingViewModel = new HowCategoryRatingViewModel()
  ) {
    this.viewModel = viewModel
    this.categoryRatingViewModel = categoryRatingViewModel
  }

  get chartData(): ComputedRef<{
    labelChartData: string[][]
    categoryRatingChartData: number[][]
    maxRating: number
  }> {
    return this.viewModel.chartData
  }

  get chartSeries(): ComputedRef<any[]> {
    return this.viewModel.chartSeries
  }

  get chartOptions(): ComputedRef<any> {
    return this.viewModel.chartOptions
  }

  get hasData(): ComputedRef<boolean> {
    return this.viewModel.hasData
  }

  get isLoading(): ComputedRef<boolean> {
    return this.categoryRatingViewModel.isLoading
  }

  get error(): ComputedRef<string | null> {
    return this.categoryRatingViewModel.error
  }

  initChart = (
    userList: User[],
    color: string,
    otherColor: string,
    dashboardProgramLanguage: ProgramLanguageType | 'All',
    displayLanguage: DisplayLanguageType,
    isLightMode: boolean
  ) => {
    const chartData = this.getChartOfHowCategoryData(displayLanguage)

    if (chartData.labelChartData.length == 0) {
      this.viewModel.setHasData(false)
      return
    }

    try {
      const chartSeries = []
      for (let i = 0; i < userList.length; i++) {
        const roundedArray = chartData.categoryRatingChartData[i].map(num => num.toFixed(2))
        chartSeries.push(this.getChartData(userList[i].id, roundedArray, i == 0 ? color : otherColor))
      }
      const chartOptions = getRadarChartOptions(
        displayLanguage == 'Japanese'
          ? 'アルゴリズム別レーティング: ' + dashboardProgramLanguage
          : 'Rating by Algorithm Classification (How): ' + dashboardProgramLanguage,
        color,
        chartData.labelChartData,
        isLightMode
      )
      this.viewModel.setChartData(chartData)
      this.viewModel.setChartSeries(chartSeries)
      this.viewModel.setChartOptions(chartOptions)
      this.viewModel.setHasData(true)
    } catch (error) {
      this.viewModel.setHasData(false)
    }
  }

  private getChartData = (name: string, values: any, color: string) => {
    return {
      name: name,
      data: values,
      color: color
    }
  }

  // チャート用のデータを取得
  getChartOfHowCategoryData = (displayLanguage: DisplayLanguageType) => {
    const categories = this.categoryRatingViewModel.userHowCategoryRatingList.value

    const labelChartData = this.getLabelsAnalytic(categories[0], displayLanguage)
    const categoryRatingChartData: number[][] = []
    let maxRating = 0

    for (let i = 0; i < categories.length; i++) {
      const ratings = this.getCategoryRatingData(categories[i])
      categoryRatingChartData.push(ratings)

      if (ratings.length == 0) categoryRatingChartData[i].push(0)

      maxRating = Math.max(
        maxRating,
        ratings.map(v => v).reduce((a, b) => Math.max(a, b))
      )
    }

    return { labelChartData, categoryRatingChartData, maxRating }
  }

  getLabelsAnalytic = (categories: CategoryRating[], displayLanguage: DisplayLanguageType) => {
    const labels: string[][] = []
    for (let i = 0; i < categories.length; i++) {
      if (displayLanguage == 'Japanese') labels.push(categoryLabelsMultipleJP[Number(categories[i].categoryId)])
      else labels.push(categoryLabelsMultipleEN[Number(categories[i].categoryId)])
    }

    return labels
  }

  getCategoryRatingData = (categories: CategoryRating[]) => {
    const ratings: number[] = []
    for (let i = 0; i < categories.length; i++) {
      ratings.push(categories[i].value)
    }

    return ratings
  }
}
